'use client';

import React, { useState, useCallback } from 'react';
import { Briefcase, Building2, DollarSign, Database, Server, ShieldAlert, Paperclip, ChevronRight, ChevronLeft, CheckCircle2, AlertTriangle, AlertOctagon, Info, Upload, X, FileText, Mail, Clock, Lock, Zap, Eye, Shield } from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface FormData {
  // Step 1 – Business Need
  projectTitle: string;
  businessJustification: string;
  requestedBy: string;
  requestorEmail: string;
  requestorDepartment: string;
  urgency: string;
  estimatedStartDate: string;

  // Step 2 – Department
  primaryDepartment: string;
  departmentHead: string;
  businessOwner: string;
  businessOwnerEmail: string;
  secondaryDepartments: string[];
  contractOwner: string;

  // Step 3 – Budget
  estimatedAnnualValue: string;
  budgetSource: string;
  multiYear: string;
  totalContractValue: string;
  competitiveBid: string;
  soleSource: boolean;
  soleSourceJustification: string;

  // Step 4 – Data
  dataTypes: string[];
  piiInvolved: string;
  recordCount: string;
  dataResidency: string;
  dataSharing: string;
  dataRetention: string;
  encryptionRequired: string;

  // Step 5 – Systems
  systemsAccessed: string[];
  networkAccess: string;
  remoteAccess: string;
  privilegedAccess: string;
  hostingModel: string;
  integrations: string;

  // Step 6 – Risk Questionnaire
  riskAnswers: Record<string, string>;

  // Step 7 – Attachments
  attachments: AttachmentFile[];
  additionalNotes: string;
}

interface AttachmentFile {
  id: string;
  name: string;
  size: number;
  type: string;
  category: string;
}

interface RiskQuestion {
  id: string;
  category: string;
  question: string;
  options: { value: string; label: string; score: number }[];
  weight: number;
}

interface Reviewer {
  role: string;
  name: string;
  email: string;
  reason: string;
  required: boolean;
  icon: React.ElementType;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Business Need', icon: Briefcase, short: 'Need' },
  { id: 2, label: 'Department', icon: Building2, short: 'Dept' },
  { id: 3, label: 'Budget', icon: DollarSign, short: 'Budget' },
  { id: 4, label: 'Data', icon: Database, short: 'Data' },
  { id: 5, label: 'Systems', icon: Server, short: 'Systems' },
  { id: 6, label: 'Risk Questionnaire', icon: ShieldAlert, short: 'Risk' },
  { id: 7, label: 'Attachments', icon: Paperclip, short: 'Attach' },
];

const DEPARTMENTS = [
  'City Manager\'s Office', 'Finance', 'Human Resources', 'Information Technology',
  'Police', 'Fire', 'Public Works', 'Parks & Recreation', 'Planning & Community Development',
  'City Attorney', 'City Clerk', 'Library', 'Housing & Human Services',
];

const DATA_TYPES = [
  'Personally Identifiable Information (PII)', 'Protected Health Information (PHI)',
  'Financial Records', 'Law Enforcement Data', 'Employee Records',
  'Juvenile Records', 'Social Security Numbers', 'Payment Card Data (PCI)',
  'Confidential City Records', 'Public Records Only',
];

const SYSTEMS = [
  'Tyler Munis (ERP/Finance)', 'PeopleSoft HR', 'Accela (Permits)',
  'Salesforce CRM', 'Microsoft 365', 'Active Directory / Azure AD',
  'GIS / ESRI ArcGIS', 'CAD / RMS (Police)', 'SCADA / ICS',
  'City Website / CMS', 'Payment Processing Systems', 'No City Systems',
];

const RISK_QUESTIONS: RiskQuestion[] = [
  {
    id: 'q1', category: 'Data Sensitivity',
    question: 'What is the highest sensitivity level of data this vendor will access or process?',
    weight: 3,
    options: [
      { value: 'none', label: 'No sensitive data — public records only', score: 0 },
      { value: 'low', label: 'Internal / non-sensitive city data', score: 1 },
      { value: 'moderate', label: 'Confidential records (financial, HR)', score: 2 },
      { value: 'high', label: 'PII, PHI, or law enforcement data', score: 3 },
      { value: 'critical', label: 'Highly sensitive (SSN, PCI, juvenile records)', score: 4 },
    ],
  },
  {
    id: 'q2', category: 'Data Volume',
    question: 'How many individual records will the vendor access or process?',
    weight: 2,
    options: [
      { value: 'none', label: 'Zero — no individual records', score: 0 },
      { value: 'small', label: 'Fewer than 500 records', score: 1 },
      { value: 'medium', label: '500 – 10,000 records', score: 2 },
      { value: 'large', label: '10,000 – 100,000 records', score: 3 },
      { value: 'xlarge', label: 'More than 100,000 records', score: 4 },
    ],
  },
  {
    id: 'q3', category: 'System Access',
    question: 'What level of access will the vendor have to city systems or infrastructure?',
    weight: 3,
    options: [
      { value: 'none', label: 'No system access required', score: 0 },
      { value: 'readonly', label: 'Read-only access to non-critical systems', score: 1 },
      { value: 'limited', label: 'Limited write access to non-critical systems', score: 2 },
      { value: 'broad', label: 'Broad access to multiple city systems', score: 3 },
      { value: 'privileged', label: 'Privileged / admin access or critical infrastructure', score: 4 },
    ],
  },
  {
    id: 'q4', category: 'Operational Criticality',
    question: 'What is the impact if this vendor\'s service is unavailable for 24+ hours?',
    weight: 3,
    options: [
      { value: 'none', label: 'No impact — service is non-essential', score: 0 },
      { value: 'minor', label: 'Minor inconvenience, workarounds available', score: 1 },
      { value: 'moderate', label: 'Moderate disruption to internal operations', score: 2 },
      { value: 'significant', label: 'Significant disruption to public-facing services', score: 3 },
      { value: 'critical', label: 'Critical — public safety or essential services impacted', score: 4 },
    ],
  },
  {
    id: 'q5', category: 'Financial Exposure',
    question: 'What is the total estimated contract value (all years)?',
    weight: 2,
    options: [
      { value: 'micro', label: 'Under $10,000', score: 0 },
      { value: 'small', label: '$10,000 – $50,000', score: 1 },
      { value: 'medium', label: '$50,000 – $250,000', score: 2 },
      { value: 'large', label: '$250,000 – $1,000,000', score: 3 },
      { value: 'xlarge', label: 'Over $1,000,000', score: 4 },
    ],
  },
  {
    id: 'q6', category: 'Regulatory Compliance',
    question: 'Are there specific regulatory or compliance requirements this engagement must satisfy?',
    weight: 2,
    options: [
      { value: 'none', label: 'No specific regulatory requirements', score: 0 },
      { value: 'minor', label: 'General public sector procurement rules only', score: 1 },
      { value: 'moderate', label: 'HIPAA, CJIS, or similar sector-specific rules', score: 2 },
      { value: 'significant', label: 'Multiple overlapping frameworks (HIPAA + CJIS, etc.)', score: 3 },
      { value: 'critical', label: 'Federal mandates with audit/penalty exposure', score: 4 },
    ],
  },
  {
    id: 'q7', category: 'Vendor Maturity',
    question: 'What is the vendor\'s security and compliance posture?',
    weight: 2,
    options: [
      { value: 'certified', label: 'SOC 2 Type II / ISO 27001 certified', score: 0 },
      { value: 'attested', label: 'Self-attested with supporting documentation', score: 1 },
      { value: 'partial', label: 'Partial documentation available', score: 2 },
      { value: 'minimal', label: 'Minimal documentation, no formal program', score: 3 },
      { value: 'unknown', label: 'Unknown — new or unvetted vendor', score: 4 },
    ],
  },
  {
    id: 'q8', category: 'Geographic & Subcontractor Risk',
    question: 'Where will data be processed or stored, and are subcontractors involved?',
    weight: 1,
    options: [
      { value: 'local', label: 'On-premises at city facilities, no subcontractors', score: 0 },
      { value: 'domestic', label: 'US-based cloud, no subcontractors', score: 1 },
      { value: 'domestic_sub', label: 'US-based cloud with US subcontractors', score: 2 },
      { value: 'international', label: 'International data processing or storage', score: 3 },
      { value: 'unknown', label: 'Unknown data residency or subcontractor chain', score: 4 },
    ],
  },
];

const MAX_POSSIBLE_SCORE = RISK_QUESTIONS.reduce((sum, q) => sum + q.weight * 4, 0);

// ─── Risk Scoring ─────────────────────────────────────────────────────────────

function computeRiskScore(answers: Record<string, string>): {
  raw: number;
  max: number;
  pct: number;
  tier: 'Critical' | 'High' | 'Moderate' | 'Low';
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  breakdown: { category: string; score: number; max: number }[];
} {
  let raw = 0;
  const breakdown: { category: string; score: number; max: number }[] = [];

  RISK_QUESTIONS.forEach((q) => {
    const answer = answers[q.id];
    const option = q.options.find((o) => o.value === answer);
    const score = option ? option.score * q.weight : 0;
    raw += score;
    breakdown.push({ category: q.category, score, max: q.weight * 4 });
  });

  const pct = Math.round((raw / MAX_POSSIBLE_SCORE) * 100);

  let tier: 'Critical' | 'High' | 'Moderate' | 'Low';
  let label: string;
  let color: string;
  let bgColor: string;
  let borderColor: string;

  if (pct >= 70) {
    tier = 'Critical'; label = 'Critical Risk';
    color = 'text-red-700 dark:text-red-400';
    bgColor = 'bg-red-50 dark:bg-red-950/40';
    borderColor = 'border-red-200 dark:border-red-800';
  } else if (pct >= 45) {
    tier = 'High'; label = 'High Risk';
    color = 'text-orange-700 dark:text-orange-400';
    bgColor = 'bg-orange-50 dark:bg-orange-950/40';
    borderColor = 'border-orange-200 dark:border-orange-800';
  } else if (pct >= 20) {
    tier = 'Moderate'; label = 'Moderate Risk';
    color = 'text-amber-700 dark:text-amber-400';
    bgColor = 'bg-amber-50 dark:bg-amber-950/40';
    borderColor = 'border-amber-200 dark:border-amber-800';
  } else {
    tier = 'Low'; label = 'Low Risk';
    color = 'text-teal-700 dark:text-teal-400';
    bgColor = 'bg-teal-50 dark:bg-teal-950/40';
    borderColor = 'border-teal-200 dark:border-teal-800';
  }

  return { raw, max: MAX_POSSIBLE_SCORE, pct, tier, label, color, bgColor, borderColor, breakdown };
}

// ─── Reviewer Routing ─────────────────────────────────────────────────────────

function computeReviewers(
  riskResult: ReturnType<typeof computeRiskScore>,
  formData: FormData
): Reviewer[] {
  const reviewers: Reviewer[] = [];

  // Always required
  reviewers.push({
    role: 'Procurement Officer',
    name: 'Marcus Webb',
    email: 'mwebb@pleasanton.gov',
    reason: 'Required for all vendor intake submissions',
    required: true,
    icon: Briefcase,
  });

  // Department head always required
  if (formData.departmentHead) {
    reviewers.push({
      role: 'Department Head',
      name: formData.departmentHead || 'Department Head',
      email: `dept-head@pleasanton.gov`,
      reason: `Primary department owner approval for ${formData.primaryDepartment || 'selected department'}`,
      required: true,
      icon: Building2,
    });
  }

  // IT Security for any system access or data
  const hasSystemAccess = formData.systemsAccessed.length > 0 && !formData.systemsAccessed.includes('No City Systems');
  const hasSensitiveData = formData.dataTypes.some(d =>
    ['Personally Identifiable Information (PII)', 'Protected Health Information (PHI)',
      'Law Enforcement Data', 'Social Security Numbers', 'Payment Card Data (PCI)'].includes(d)
  );

  if (hasSystemAccess || hasSensitiveData || ['Critical', 'High'].includes(riskResult.tier)) {
    reviewers.push({
      role: 'IT Security Manager',
      name: 'Priya Nair',
      email: 'pnair-security@pleasanton.gov',
      reason: hasSystemAccess
        ? 'Vendor requires access to city systems'
        : 'Sensitive data handling requires IT Security review',
      required: true,
      icon: Shield,
    });
  }

  // Privacy Officer for PII/PHI
  if (formData.dataTypes.includes('Personally Identifiable Information (PII)') ||
    formData.dataTypes.includes('Protected Health Information (PHI)') ||
    formData.dataTypes.includes('Social Security Numbers')) {
    reviewers.push({
      role: 'Privacy Officer',
      name: 'Sandra Okafor',
      email: 'sokafor-privacy@pleasanton.gov',
      reason: 'PII/PHI data handling requires Privacy Officer review',
      required: true,
      icon: Eye,
    });
  }

  // City Attorney for high-value or critical
  const contractVal = parseFloat(formData.totalContractValue.replace(/[^0-9.]/g, '')) || 0;
  if (contractVal >= 250000 || riskResult.tier === 'Critical') {
    reviewers.push({
      role: 'City Attorney',
      name: 'Robert Castillo',
      email: 'rcastillo-legal@pleasanton.gov',
      reason: contractVal >= 250000
        ? `Contract value ($${contractVal.toLocaleString()}) requires legal review`
        : 'Critical risk tier requires City Attorney review',
      required: true,
      icon: FileText,
    });
  }

  // Finance for large budgets
  if (contractVal >= 100000 || formData.budgetSource === 'Federal Grant' || formData.budgetSource === 'State Grant') {
    reviewers.push({
      role: 'Finance Director',
      name: 'Angela Torres',
      email: 'atorres-finance@pleasanton.gov',
      reason: contractVal >= 100000
        ? 'Budget threshold requires Finance Director approval' :'Grant-funded engagement requires Finance review',
      required: contractVal >= 250000,
      icon: DollarSign,
    });
  }

  // CISO for critical risk
  if (riskResult.tier === 'Critical') {
    reviewers.push({
      role: 'Chief Information Security Officer',
      name: 'James Okonkwo',
      email: 'jokonkwo-ciso@pleasanton.gov',
      reason: 'Critical inherent risk score requires CISO sign-off',
      required: true,
      icon: AlertOctagon,
    });
  }

  // Law Enforcement data
  if (formData.dataTypes.includes('Law Enforcement Data') || formData.dataTypes.includes('Juvenile Records')) {
    reviewers.push({
      role: 'Police Chief / Records Custodian',
      name: 'Chief David Park',
      email: 'dpark-police@pleasanton.gov',
      reason: 'Law enforcement or juvenile records require Police Chief authorization',
      required: true,
      icon: Lock,
    });
  }

  return reviewers;
}

// ─── Initial State ────────────────────────────────────────────────────────────

const INITIAL_FORM: FormData = {
  projectTitle: '', businessJustification: '', requestedBy: '', requestorEmail: '',
  requestorDepartment: '', urgency: '', estimatedStartDate: '',
  primaryDepartment: '', departmentHead: '', businessOwner: '', businessOwnerEmail: '',
  secondaryDepartments: [], contractOwner: '',
  estimatedAnnualValue: '', budgetSource: '', multiYear: '', totalContractValue: '',
  competitiveBid: '', soleSource: false, soleSourceJustification: '',
  dataTypes: [], piiInvolved: '', recordCount: '', dataResidency: '',
  dataSharing: '', dataRetention: '', encryptionRequired: '',
  systemsAccessed: [], networkAccess: '', remoteAccess: '', privilegedAccess: '',
  hostingModel: '', integrations: '',
  riskAnswers: {},
  attachments: [], additionalNotes: '',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="block text-xs font-semibold text-[hsl(var(--text-secondary))] uppercase tracking-wide mb-1.5">
      {children}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );
}

function Input({ value, onChange, placeholder, type = 'text', className = '' }: {
  value: string; onChange: (v: string) => void; placeholder?: string; type?: string; className?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full px-3 py-2 text-sm rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] placeholder:text-[hsl(var(--text-muted))] focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors ${className}`}
    />
  );
}

function Select({ value, onChange, options, placeholder }: {
  value: string; onChange: (v: string) => void;
  options: { value: string; label: string }[]; placeholder?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 text-sm rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function Textarea({ value, onChange, placeholder, rows = 3 }: {
  value: string; onChange: (v: string) => void; placeholder?: string; rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full px-3 py-2 text-sm rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] placeholder:text-[hsl(var(--text-muted))] focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors resize-none"
    />
  );
}

function MultiCheckbox({ options, selected, onChange }: {
  options: string[]; selected: string[]; onChange: (v: string[]) => void;
}) {
  const toggle = (item: string) => {
    onChange(selected.includes(item) ? selected.filter((s) => s !== item) : [...selected, item]);
  };
  return (
    <div className="grid grid-cols-1 gap-1.5">
      {options.map((opt) => (
        <label key={opt} className="flex items-center gap-2.5 cursor-pointer group">
          <div
            onClick={() => toggle(opt)}
            className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer ${
              selected.includes(opt)
                ? 'bg-teal-600 border-teal-600' :'border-[hsl(var(--border))] group-hover:border-teal-400'
            }`}
          >
            {selected.includes(opt) && (
              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <span className="text-sm text-[hsl(var(--text-primary))]">{opt}</span>
        </label>
      ))}
    </div>
  );
}

function RadioGroup({ options, value, onChange }: {
  options: { value: string; label: string; score?: number }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      {options.map((opt) => (
        <label
          key={opt.value}
          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
            value === opt.value
              ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30' :'border-[hsl(var(--border))] hover:border-teal-300 hover:bg-[hsl(var(--surface))]'
          }`}
        >
          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
            value === opt.value ? 'border-teal-600' : 'border-[hsl(var(--border))]'
          }`}>
            {value === opt.value && <div className="w-2 h-2 rounded-full bg-teal-600" />}
          </div>
          <span className="text-sm text-[hsl(var(--text-primary))] leading-snug">{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

// ─── Step Components ──────────────────────────────────────────────────────────

function StepBusinessNeed({ data, update }: { data: FormData; update: (k: keyof FormData, v: unknown) => void }) {
  return (
    <div className="space-y-5">
      <div>
        <FieldLabel required>Project / Engagement Title</FieldLabel>
        <Input value={data.projectTitle} onChange={(v) => update('projectTitle', v)} placeholder="e.g., IT Managed Services — Network Infrastructure" />
      </div>
      <div>
        <FieldLabel required>Business Justification</FieldLabel>
        <Textarea value={data.businessJustification} onChange={(v) => update('businessJustification', v)}
          placeholder="Describe the business need, problem being solved, and expected outcomes..." rows={4} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <FieldLabel required>Requested By</FieldLabel>
          <Input value={data.requestedBy} onChange={(v) => update('requestedBy', v)} placeholder="Full name" />
        </div>
        <div>
          <FieldLabel required>Requestor Email</FieldLabel>
          <Input value={data.requestorEmail} onChange={(v) => update('requestorEmail', v)} type="email" placeholder="name@pleasanton.gov" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <FieldLabel required>Requestor Department</FieldLabel>
          <Select value={data.requestorDepartment} onChange={(v) => update('requestorDepartment', v)}
            options={DEPARTMENTS.map((d) => ({ value: d, label: d }))} placeholder="Select department" />
        </div>
        <div>
          <FieldLabel required>Urgency</FieldLabel>
          <Select value={data.urgency} onChange={(v) => update('urgency', v)}
            options={[
              { value: 'routine', label: 'Routine (30+ days)' },
              { value: 'standard', label: 'Standard (15–30 days)' },
              { value: 'expedited', label: 'Expedited (7–14 days)' },
              { value: 'emergency', label: 'Emergency (<7 days)' },
            ]}
            placeholder="Select urgency" />
        </div>
      </div>
      <div>
        <FieldLabel>Estimated Start Date</FieldLabel>
        <Input value={data.estimatedStartDate} onChange={(v) => update('estimatedStartDate', v)} type="date" />
      </div>
    </div>
  );
}

function StepDepartment({ data, update }: { data: FormData; update: (k: keyof FormData, v: unknown) => void }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <FieldLabel required>Primary Department</FieldLabel>
          <Select value={data.primaryDepartment} onChange={(v) => update('primaryDepartment', v)}
            options={DEPARTMENTS.map((d) => ({ value: d, label: d }))} placeholder="Select department" />
        </div>
        <div>
          <FieldLabel required>Department Head</FieldLabel>
          <Input value={data.departmentHead} onChange={(v) => update('departmentHead', v)} placeholder="Full name" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <FieldLabel required>Business Owner</FieldLabel>
          <Input value={data.businessOwner} onChange={(v) => update('businessOwner', v)} placeholder="Full name" />
        </div>
        <div>
          <FieldLabel required>Business Owner Email</FieldLabel>
          <Input value={data.businessOwnerEmail} onChange={(v) => update('businessOwnerEmail', v)} type="email" placeholder="name@pleasanton.gov" />
        </div>
      </div>
      <div>
        <FieldLabel>Contract Owner</FieldLabel>
        <Input value={data.contractOwner} onChange={(v) => update('contractOwner', v)} placeholder="Person responsible for contract management" />
      </div>
      <div>
        <FieldLabel>Secondary / Impacted Departments</FieldLabel>
        <p className="text-xs text-[hsl(var(--text-muted))] mb-2">Select all departments that will use or be impacted by this vendor</p>
        <div className="max-h-48 overflow-y-auto border border-[hsl(var(--border))] rounded-lg p-3">
          <MultiCheckbox
            options={DEPARTMENTS.filter((d) => d !== data.primaryDepartment)}
            selected={data.secondaryDepartments}
            onChange={(v) => update('secondaryDepartments', v)}
          />
        </div>
      </div>
    </div>
  );
}

function StepBudget({ data, update }: { data: FormData; update: (k: keyof FormData, v: unknown) => void }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <FieldLabel required>Estimated Annual Value</FieldLabel>
          <Input value={data.estimatedAnnualValue} onChange={(v) => update('estimatedAnnualValue', v)} placeholder="$0.00" />
        </div>
        <div>
          <FieldLabel required>Total Contract Value</FieldLabel>
          <Input value={data.totalContractValue} onChange={(v) => update('totalContractValue', v)} placeholder="$0.00" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <FieldLabel required>Budget Source</FieldLabel>
          <Select value={data.budgetSource} onChange={(v) => update('budgetSource', v)}
            options={[
              { value: 'General Fund', label: 'General Fund' },
              { value: 'Capital Budget', label: 'Capital Budget' },
              { value: 'Federal Grant', label: 'Federal Grant' },
              { value: 'State Grant', label: 'State Grant' },
              { value: 'Enterprise Fund', label: 'Enterprise Fund' },
              { value: 'Special Revenue', label: 'Special Revenue Fund' },
              { value: 'Other', label: 'Other' },
            ]}
            placeholder="Select budget source" />
        </div>
        <div>
          <FieldLabel required>Multi-Year Contract?</FieldLabel>
          <Select value={data.multiYear} onChange={(v) => update('multiYear', v)}
            options={[
              { value: 'no', label: 'No — single year' },
              { value: '2yr', label: 'Yes — 2 years' },
              { value: '3yr', label: 'Yes — 3 years' },
              { value: '4yr', label: 'Yes — 4 years' },
              { value: '5yr', label: 'Yes — 5 years' },
              { value: '5yr+', label: 'Yes — more than 5 years' },
            ]}
            placeholder="Select term" />
        </div>
      </div>
      <div>
        <FieldLabel required>Procurement Method</FieldLabel>
        <Select value={data.competitiveBid} onChange={(v) => update('competitiveBid', v)}
          options={[
            { value: 'rfp', label: 'RFP — Request for Proposal' },
            { value: 'rfq', label: 'RFQ — Request for Quote' },
            { value: 'ifb', label: 'IFB — Invitation for Bid' },
            { value: 'piggyback', label: 'Cooperative / Piggyback Contract' },
            { value: 'sole_source', label: 'Sole Source' },
            { value: 'emergency', label: 'Emergency Procurement' },
          ]}
          placeholder="Select procurement method" />
      </div>
      {data.competitiveBid === 'sole_source' && (
        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
          <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1.5">
            <AlertTriangle size={13} /> Sole Source Justification Required
          </p>
          <Textarea value={data.soleSourceJustification} onChange={(v) => update('soleSourceJustification', v)}
            placeholder="Provide detailed justification for sole source procurement..." rows={3} />
        </div>
      )}
    </div>
  );
}

function StepData({ data, update }: { data: FormData; update: (k: keyof FormData, v: unknown) => void }) {
  return (
    <div className="space-y-5">
      <div>
        <FieldLabel required>Data Types Involved</FieldLabel>
        <p className="text-xs text-[hsl(var(--text-muted))] mb-2">Select all types of data this vendor will access, process, or store</p>
        <div className="border border-[hsl(var(--border))] rounded-lg p-3">
          <MultiCheckbox options={DATA_TYPES} selected={data.dataTypes} onChange={(v) => update('dataTypes', v)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <FieldLabel>Estimated Record Count</FieldLabel>
          <Select value={data.recordCount} onChange={(v) => update('recordCount', v)}
            options={[
              { value: 'none', label: 'None / Not applicable' },
              { value: '<500', label: 'Fewer than 500' },
              { value: '500-10k', label: '500 – 10,000' },
              { value: '10k-100k', label: '10,000 – 100,000' },
              { value: '>100k', label: 'More than 100,000' },
            ]}
            placeholder="Select range" />
        </div>
        <div>
          <FieldLabel>Data Residency</FieldLabel>
          <Select value={data.dataResidency} onChange={(v) => update('dataResidency', v)}
            options={[
              { value: 'on_prem', label: 'On-premises (city facilities)' },
              { value: 'us_cloud', label: 'US-based cloud' },
              { value: 'international', label: 'International' },
              { value: 'unknown', label: 'Unknown' },
            ]}
            placeholder="Select location" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <FieldLabel>Data Sharing with Third Parties?</FieldLabel>
          <Select value={data.dataSharing} onChange={(v) => update('dataSharing', v)}
            options={[
              { value: 'no', label: 'No — vendor only' },
              { value: 'subcontractors', label: 'Yes — subcontractors' },
              { value: 'partners', label: 'Yes — partner organizations' },
              { value: 'unknown', label: 'Unknown' },
            ]}
            placeholder="Select option" />
        </div>
        <div>
          <FieldLabel>Encryption Required?</FieldLabel>
          <Select value={data.encryptionRequired} onChange={(v) => update('encryptionRequired', v)}
            options={[
              { value: 'yes_transit_rest', label: 'Yes — in transit and at rest' },
              { value: 'yes_transit', label: 'Yes — in transit only' },
              { value: 'no', label: 'No — not required' },
              { value: 'unknown', label: 'Unknown' },
            ]}
            placeholder="Select option" />
        </div>
      </div>
    </div>
  );
}

function StepSystems({ data, update }: { data: FormData; update: (k: keyof FormData, v: unknown) => void }) {
  return (
    <div className="space-y-5">
      <div>
        <FieldLabel required>City Systems Accessed</FieldLabel>
        <p className="text-xs text-[hsl(var(--text-muted))] mb-2">Select all city systems this vendor will access or integrate with</p>
        <div className="border border-[hsl(var(--border))] rounded-lg p-3">
          <MultiCheckbox options={SYSTEMS} selected={data.systemsAccessed} onChange={(v) => update('systemsAccessed', v)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <FieldLabel>Network Access Required?</FieldLabel>
          <Select value={data.networkAccess} onChange={(v) => update('networkAccess', v)}
            options={[
              { value: 'none', label: 'None' },
              { value: 'internet_only', label: 'Internet only (no city network)' },
              { value: 'city_network', label: 'City network access' },
              { value: 'restricted_vlan', label: 'Restricted VLAN only' },
            ]}
            placeholder="Select option" />
        </div>
        <div>
          <FieldLabel>Remote Access?</FieldLabel>
          <Select value={data.remoteAccess} onChange={(v) => update('remoteAccess', v)}
            options={[
              { value: 'no', label: 'No remote access' },
              { value: 'vpn', label: 'VPN access' },
              { value: 'rdp', label: 'Remote Desktop (RDP)' },
              { value: 'web_portal', label: 'Web portal only' },
            ]}
            placeholder="Select option" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <FieldLabel>Privileged / Admin Access?</FieldLabel>
          <Select value={data.privilegedAccess} onChange={(v) => update('privilegedAccess', v)}
            options={[
              { value: 'no', label: 'No privileged access' },
              { value: 'limited', label: 'Limited admin (specific systems)' },
              { value: 'broad', label: 'Broad admin access' },
              { value: 'root', label: 'Root / full system access' },
            ]}
            placeholder="Select option" />
        </div>
        <div>
          <FieldLabel>Hosting Model</FieldLabel>
          <Select value={data.hostingModel} onChange={(v) => update('hostingModel', v)}
            options={[
              { value: 'saas', label: 'SaaS — vendor hosted' },
              { value: 'on_prem', label: 'On-premises at city' },
              { value: 'hybrid', label: 'Hybrid' },
              { value: 'paas', label: 'PaaS / IaaS' },
            ]}
            placeholder="Select model" />
        </div>
      </div>
      <div>
        <FieldLabel>Integration Details</FieldLabel>
        <Textarea value={data.integrations} onChange={(v) => update('integrations', v)}
          placeholder="Describe any API integrations, data feeds, or automated connections..." rows={2} />
      </div>
    </div>
  );
}

function StepRiskQuestionnaire({ data, update }: { data: FormData; update: (k: keyof FormData, v: unknown) => void }) {
  const answeredCount = Object.keys(data.riskAnswers).length;
  const riskResult = answeredCount === RISK_QUESTIONS.length ? computeRiskScore(data.riskAnswers) : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[hsl(var(--text-secondary))]">
          Answer all {RISK_QUESTIONS.length} questions to generate your inherent risk score.
        </p>
        <span className="text-xs font-semibold text-[hsl(var(--text-muted))] bg-[hsl(var(--surface))] border border-[hsl(var(--border))] px-2.5 py-1 rounded-full">
          {answeredCount} / {RISK_QUESTIONS.length} answered
        </span>
      </div>

      {RISK_QUESTIONS.map((q, idx) => (
        <div key={q.id} className="border border-[hsl(var(--border))] rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-[hsl(var(--surface))] border-b border-[hsl(var(--border))] flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-teal-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
              {idx + 1}
            </span>
            <div>
              <p className="text-2xs font-semibold text-teal-600 uppercase tracking-wide mb-0.5">{q.category}</p>
              <p className="text-sm font-medium text-[hsl(var(--text-primary))]">{q.question}</p>
            </div>
          </div>
          <div className="p-4">
            <RadioGroup
              options={q.options}
              value={data.riskAnswers[q.id] || ''}
              onChange={(v) => update('riskAnswers', { ...data.riskAnswers, [q.id]: v })}
            />
          </div>
        </div>
      ))}

      {riskResult && (
        <div className={`rounded-xl border-2 p-5 ${riskResult.bgColor} ${riskResult.borderColor}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {riskResult.tier === 'Critical' && <AlertOctagon size={22} className={riskResult.color} />}
              {riskResult.tier === 'High' && <AlertTriangle size={22} className={riskResult.color} />}
              {riskResult.tier === 'Moderate' && <Info size={22} className={riskResult.color} />}
              {riskResult.tier === 'Low' && <CheckCircle2 size={22} className={riskResult.color} />}
              <div>
                <p className={`text-lg font-bold ${riskResult.color}`}>{riskResult.label}</p>
                <p className="text-xs text-[hsl(var(--text-muted))]">Inherent Risk Score</p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-3xl font-bold tabular-nums ${riskResult.color}`}>{riskResult.pct}%</p>
              <p className="text-xs text-[hsl(var(--text-muted))]">{riskResult.raw} / {riskResult.max} pts</p>
            </div>
          </div>
          <div className="w-full bg-white/50 dark:bg-black/20 rounded-full h-2 mb-4">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${
                riskResult.tier === 'Critical' ? 'bg-red-600' :
                riskResult.tier === 'High' ? 'bg-orange-500' :
                riskResult.tier === 'Moderate' ? 'bg-amber-500' : 'bg-teal-500'
              }`}
              style={{ width: `${riskResult.pct}%` }}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {riskResult.breakdown.map((b) => (
              <div key={b.category} className="flex items-center justify-between text-xs">
                <span className="text-[hsl(var(--text-secondary))]">{b.category}</span>
                <span className={`font-semibold ${riskResult.color}`}>{b.score}/{b.max}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StepAttachments({ data, update }: { data: FormData; update: (k: keyof FormData, v: unknown) => void }) {
  const ATTACHMENT_CATEGORIES = [
    'Business Case / Justification', 'Vendor Proposal / Quote', 'Security Assessment',
    'Insurance Certificate', 'References', 'Sole Source Justification', 'Other',
  ];

  const addMockFile = (category: string) => {
    const newFile: AttachmentFile = {
      id: `att-${Date.now()}`,
      name: `${category.replace(/\s+/g, '_')}_document.pdf`,
      size: Math.floor(Math.random() * 2000000) + 100000,
      type: 'application/pdf',
      category,
    };
    update('attachments', [...data.attachments, newFile]);
  };

  const removeFile = (id: string) => {
    update('attachments', data.attachments.filter((f) => f.id !== id));
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-5">
      <div>
        <FieldLabel>Upload Supporting Documents</FieldLabel>
        <p className="text-xs text-[hsl(var(--text-muted))] mb-3">Attach relevant documents to support this intake request. Accepted: PDF, DOCX, XLSX, PNG, JPG (max 25 MB each)</p>

        <div className="border-2 border-dashed border-[hsl(var(--border))] rounded-xl p-6 text-center hover:border-teal-400 transition-colors">
          <Upload size={28} className="mx-auto text-[hsl(var(--text-muted))] mb-2" />
          <p className="text-sm font-medium text-[hsl(var(--text-primary))] mb-1">Drop files here or click to browse</p>
          <p className="text-xs text-[hsl(var(--text-muted))] mb-4">Simulated upload — click a category below to add a sample file</p>
          <div className="flex flex-wrap gap-2 justify-center">
            {ATTACHMENT_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => addMockFile(cat)}
                className="text-xs px-3 py-1.5 rounded-full border border-[hsl(var(--border))] text-[hsl(var(--text-secondary))] hover:border-teal-400 hover:text-teal-600 transition-colors"
              >
                + {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {data.attachments.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-[hsl(var(--text-secondary))] uppercase tracking-wide">
            Attached Files ({data.attachments.length})
          </p>
          {data.attachments.map((file) => (
            <div key={file.id} className="flex items-center gap-3 p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
              <FileText size={16} className="text-teal-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[hsl(var(--text-primary))] truncate">{file.name}</p>
                <p className="text-xs text-[hsl(var(--text-muted))]">{file.category} · {formatSize(file.size)}</p>
              </div>
              <button onClick={() => removeFile(file.id)} className="text-[hsl(var(--text-muted))] hover:text-red-500 transition-colors">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div>
        <FieldLabel>Additional Notes</FieldLabel>
        <Textarea value={data.additionalNotes} onChange={(v) => update('additionalNotes', v)}
          placeholder="Any additional context, special considerations, or instructions for reviewers..." rows={4} />
      </div>
    </div>
  );
}

// ─── Review & Submit ──────────────────────────────────────────────────────────

function ReviewSubmit({ data, onEdit }: { data: FormData; onEdit: (step: number) => void }) {
  const riskResult = computeRiskScore(data.riskAnswers);
  const reviewers = computeReviewers(riskResult, data);
  const requiredReviewers = reviewers.filter((r) => r.required);
  const optionalReviewers = reviewers.filter((r) => !r.required);

  return (
    <div className="space-y-6">
      {/* Risk Score Banner */}
      <div className={`rounded-xl border-2 p-5 ${riskResult.bgColor} ${riskResult.borderColor}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {riskResult.tier === 'Critical' && <AlertOctagon size={24} className={riskResult.color} />}
            {riskResult.tier === 'High' && <AlertTriangle size={24} className={riskResult.color} />}
            {riskResult.tier === 'Moderate' && <Info size={24} className={riskResult.color} />}
            {riskResult.tier === 'Low' && <CheckCircle2 size={24} className={riskResult.color} />}
            <div>
              <p className={`text-xl font-bold ${riskResult.color}`}>{riskResult.label}</p>
              <p className="text-xs text-[hsl(var(--text-muted))]">Auto-generated inherent risk score</p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-4xl font-bold tabular-nums ${riskResult.color}`}>{riskResult.pct}%</p>
            <p className="text-xs text-[hsl(var(--text-muted))]">{riskResult.raw} / {riskResult.max} pts</p>
          </div>
        </div>
        <div className="w-full bg-white/50 dark:bg-black/20 rounded-full h-2.5 mt-4">
          <div
            className={`h-2.5 rounded-full transition-all duration-700 ${
              riskResult.tier === 'Critical' ? 'bg-red-600' :
              riskResult.tier === 'High' ? 'bg-orange-500' :
              riskResult.tier === 'Moderate' ? 'bg-amber-500' : 'bg-teal-500'
            }`}
            style={{ width: `${riskResult.pct}%` }}
          />
        </div>
      </div>

      {/* Reviewer Routing */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[hsl(var(--text-primary))]">Required Reviewer Routing</h3>
          <span className="text-xs text-[hsl(var(--text-muted))] bg-[hsl(var(--surface))] border border-[hsl(var(--border))] px-2 py-0.5 rounded-full">
            {requiredReviewers.length} required · {optionalReviewers.length} optional
          </span>
        </div>
        <div className="space-y-2">
          {requiredReviewers.map((r, i) => {
            const RIcon = r.icon;
            return (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
                <div className="w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-950 flex items-center justify-center flex-shrink-0">
                  <RIcon size={14} className="text-teal-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-[hsl(var(--text-primary))]">{r.name}</p>
                    <span className="text-2xs font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400">Required</span>
                  </div>
                  <p className="text-xs text-[hsl(var(--text-muted))]">{r.role} · {r.email}</p>
                  <p className="text-xs text-[hsl(var(--text-secondary))] mt-0.5">{r.reason}</p>
                </div>
              </div>
            );
          })}
          {optionalReviewers.map((r, i) => {
            const RIcon = r.icon;
            return (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--background))]">
                <div className="w-8 h-8 rounded-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] flex items-center justify-center flex-shrink-0">
                  <RIcon size={14} className="text-[hsl(var(--text-muted))]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-[hsl(var(--text-primary))]">{r.name}</p>
                    <span className="text-2xs font-semibold px-1.5 py-0.5 rounded-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] text-[hsl(var(--text-muted))]">Optional</span>
                  </div>
                  <p className="text-xs text-[hsl(var(--text-muted))]">{r.role} · {r.email}</p>
                  <p className="text-xs text-[hsl(var(--text-secondary))] mt-0.5">{r.reason}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary Sections */}
      {[
        { label: 'Business Need', step: 1, items: [
          { k: 'Project Title', v: data.projectTitle },
          { k: 'Requestor', v: `${data.requestedBy} (${data.requestorEmail})` },
          { k: 'Department', v: data.requestorDepartment },
          { k: 'Urgency', v: data.urgency },
        ]},
        { label: 'Department', step: 2, items: [
          { k: 'Primary Dept', v: data.primaryDepartment },
          { k: 'Business Owner', v: `${data.businessOwner} (${data.businessOwnerEmail})` },
          { k: 'Contract Owner', v: data.contractOwner },
        ]},
        { label: 'Budget', step: 3, items: [
          { k: 'Annual Value', v: data.estimatedAnnualValue },
          { k: 'Total Value', v: data.totalContractValue },
          { k: 'Budget Source', v: data.budgetSource },
          { k: 'Procurement', v: data.competitiveBid },
        ]},
        { label: 'Data', step: 4, items: [
          { k: 'Data Types', v: data.dataTypes.join(', ') || 'None selected' },
          { k: 'Record Count', v: data.recordCount },
          { k: 'Data Residency', v: data.dataResidency },
        ]},
        { label: 'Systems', step: 5, items: [
          { k: 'Systems', v: data.systemsAccessed.join(', ') || 'None selected' },
          { k: 'Network Access', v: data.networkAccess },
          { k: 'Hosting Model', v: data.hostingModel },
        ]},
      ].map((section) => (
        <div key={section.label} className="border border-[hsl(var(--border))] rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 bg-[hsl(var(--surface))] border-b border-[hsl(var(--border))]">
            <p className="text-xs font-bold text-[hsl(var(--text-primary))] uppercase tracking-wide">{section.label}</p>
            <button onClick={() => onEdit(section.step)} className="text-xs text-teal-600 hover:text-teal-700 font-medium">Edit</button>
          </div>
          <div className="px-4 py-3 grid grid-cols-2 gap-x-6 gap-y-2">
            {section.items.map((item) => (
              <div key={item.k}>
                <p className="text-2xs text-[hsl(var(--text-muted))] uppercase tracking-wide">{item.k}</p>
                <p className="text-xs text-[hsl(var(--text-primary))] font-medium mt-0.5 truncate">{item.v || '—'}</p>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex items-start gap-2.5 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
        <Info size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700 dark:text-blue-400">
          Submitting this form will create a new intake record, assign the inherent risk score of <strong>{riskResult.label}</strong>, and automatically notify {requiredReviewers.length} required reviewer{requiredReviewers.length !== 1 ? 's' : ''} to begin the approval workflow.
        </p>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function IntakeForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = useCallback((key: keyof FormData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const goNext = () => {
    if (currentStep < 7) setCurrentStep((s) => s + 1);
  };

  const goPrev = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const goToStep = (step: number) => setCurrentStep(step);

  const handleSubmit = async () => {
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1500));
    setSubmitting(false);
    setSubmitted(true);
  };

  const riskResult = computeRiskScore(formData.riskAnswers);
  const reviewers = computeReviewers(riskResult, formData);
  const answeredRisk = Object.keys(formData.riskAnswers).length;
  const isRiskComplete = answeredRisk === RISK_QUESTIONS.length;

  // ── Submitted State ──
  if (submitted) {
    const intakeId = `INT-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9000) + 1000)}`;
    return (
      <div className="min-h-full flex items-center justify-center p-8">
        <div className="max-w-lg w-full text-center">
          <div className="w-16 h-16 rounded-full bg-teal-100 dark:bg-teal-950 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={32} className="text-teal-600" />
          </div>
          <h2 className="text-2xl font-bold text-[hsl(var(--text-primary))] mb-2">Intake Submitted</h2>
          <p className="text-[hsl(var(--text-secondary))] mb-1">Your intake request has been submitted successfully.</p>
          <p className="text-sm font-semibold text-teal-600 mb-6">Reference ID: {intakeId}</p>

          <div className={`rounded-xl border-2 p-4 mb-6 text-left ${riskResult.bgColor} ${riskResult.borderColor}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-[hsl(var(--text-muted))] mb-0.5">Inherent Risk Score</p>
                <p className={`text-lg font-bold ${riskResult.color}`}>{riskResult.label} — {riskResult.pct}%</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-[hsl(var(--text-muted))] mb-0.5">Reviewers Notified</p>
                <p className="text-lg font-bold text-[hsl(var(--text-primary))]">{reviewers.filter((r) => r.required).length}</p>
              </div>
            </div>
          </div>

          <div className="space-y-2 text-left mb-6">
            {reviewers.filter((r) => r.required).map((r, i) => {
              const RIcon = r.icon;
              return (
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
                  <div className="w-7 h-7 rounded-full bg-teal-100 dark:bg-teal-950 flex items-center justify-center flex-shrink-0">
                    <RIcon size={13} className="text-teal-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[hsl(var(--text-primary))]">{r.name}</p>
                    <p className="text-2xs text-[hsl(var(--text-muted))]">{r.role}</p>
                  </div>
                  <div className="ml-auto flex items-center gap-1 text-2xs text-teal-600">
                    <Mail size={11} /> Notified
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex gap-3 justify-center">
            <button
              onClick={() => { setSubmitted(false); setCurrentStep(1); setFormData(INITIAL_FORM); }}
              className="px-5 py-2.5 rounded-lg border border-[hsl(var(--border))] text-sm font-medium text-[hsl(var(--text-primary))] hover:bg-[hsl(var(--surface))] transition-colors"
            >
              Submit Another
            </button>
            <button
              onClick={() => window.location.href = '/sourcing-workspace'}
              className="px-5 py-2.5 rounded-lg border border-teal-300 text-teal-700 dark:text-teal-400 dark:border-teal-700 text-sm font-medium hover:bg-teal-50 dark:hover:bg-teal-950/30 transition-colors"
            >
              View in Sourcing
            </button>
            <button
              onClick={() => window.location.href = '/vendor-management'}
              className="px-5 py-2.5 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 transition-colors"
            >
              View Vendor Queue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ──
  return (
    <div className="min-h-full bg-[hsl(var(--background))]">
      {/* Header */}
      <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-[hsl(var(--text-primary))]">New Vendor Intake</h1>
            <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">City of Pleasanton · Procurement & Risk</p>
          </div>
          <div className="flex items-center gap-3">
            {isRiskComplete && (
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${riskResult.bgColor} ${riskResult.borderColor} ${riskResult.color}`}>
                <Zap size={12} />
                {riskResult.label}
              </div>
            )}
            <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--text-muted))]">
              <Clock size={12} />
              <span>Step {currentStep} of 7</span>
            </div>
          </div>
        </div>
      </div>

      {/* Step Progress */}
      <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-6 py-3">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-1 overflow-x-auto pb-1">
            {STEPS.map((step, idx) => {
              const StepIcon = step.icon;
              const isCompleted = currentStep > step.id;
              const isCurrent = currentStep === step.id;
              return (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => isCompleted && goToStep(step.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                      isCurrent
                        ? 'bg-teal-600 text-white'
                        : isCompleted
                        ? 'text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-950/30 cursor-pointer' :'text-[hsl(var(--text-muted))] cursor-default'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 size={13} />
                    ) : (
                      <StepIcon size={13} />
                    )}
                    <span className="hidden sm:inline">{step.label}</span>
                    <span className="sm:hidden">{step.short}</span>
                  </button>
                  {idx < STEPS.length - 1 && (
                    <div className={`w-4 h-px flex-shrink-0 ${isCompleted ? 'bg-teal-400' : 'bg-[hsl(var(--border))]'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-[hsl(var(--border))]">
                <div className="flex items-center gap-2.5">
                  {React.createElement(STEPS[currentStep - 1].icon, { size: 16, className: 'text-teal-600' })}
                  <h2 className="text-base font-bold text-[hsl(var(--text-primary))]">
                    {currentStep === 7 && Object.keys(formData.riskAnswers).length === RISK_QUESTIONS.length
                      ? 'Review & Submit'
                      : STEPS[currentStep - 1].label}
                  </h2>
                </div>
              </div>
              <div className="p-5">
                {currentStep === 1 && <StepBusinessNeed data={formData} update={update} />}
                {currentStep === 2 && <StepDepartment data={formData} update={update} />}
                {currentStep === 3 && <StepBudget data={formData} update={update} />}
                {currentStep === 4 && <StepData data={formData} update={update} />}
                {currentStep === 5 && <StepSystems data={formData} update={update} />}
                {currentStep === 6 && <StepRiskQuestionnaire data={formData} update={update} />}
                {currentStep === 7 && (
                  <StepAttachments data={formData} update={update} />
                )}
              </div>
            </div>
          </div>

          {/* Sidebar Panel */}
          <div className="space-y-4">
            {/* Risk Score Live Preview */}
            {isRiskComplete && (
              <div className={`rounded-xl border p-4 ${riskResult.bgColor} ${riskResult.borderColor}`}>
                <p className="text-2xs font-bold uppercase tracking-wide text-[hsl(var(--text-muted))] mb-2">Inherent Risk Score</p>
                <div className="flex items-center justify-between mb-2">
                  <p className={`text-lg font-bold ${riskResult.color}`}>{riskResult.label}</p>
                  <p className={`text-2xl font-bold tabular-nums ${riskResult.color}`}>{riskResult.pct}%</p>
                </div>
                <div className="w-full bg-white/50 dark:bg-black/20 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${
                      riskResult.tier === 'Critical' ? 'bg-red-600' :
                      riskResult.tier === 'High' ? 'bg-orange-500' :
                      riskResult.tier === 'Moderate' ? 'bg-amber-500' : 'bg-teal-500'
                    }`}
                    style={{ width: `${riskResult.pct}%` }}
                  />
                </div>
              </div>
            )}

            {/* Reviewer Preview */}
            {(formData.primaryDepartment || isRiskComplete) && (
              <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
                <p className="text-2xs font-bold uppercase tracking-wide text-[hsl(var(--text-muted))] mb-3">Reviewer Routing</p>
                <div className="space-y-2">
                  {reviewers.slice(0, 4).map((r, i) => {
                    const RIcon = r.icon;
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-teal-100 dark:bg-teal-950 flex items-center justify-center flex-shrink-0">
                          <RIcon size={11} className="text-teal-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-[hsl(var(--text-primary))] truncate">{r.name}</p>
                          <p className="text-2xs text-[hsl(var(--text-muted))] truncate">{r.role}</p>
                        </div>
                        {r.required && (
                          <span className="ml-auto text-2xs font-semibold text-red-600 flex-shrink-0">Req</span>
                        )}
                      </div>
                    );
                  })}
                  {reviewers.length > 4 && (
                    <p className="text-2xs text-[hsl(var(--text-muted))] pl-8">+{reviewers.length - 4} more reviewer{reviewers.length - 4 !== 1 ? 's' : ''}</p>
                  )}
                </div>
              </div>
            )}

            {/* Progress */}
            <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
              <p className="text-2xs font-bold uppercase tracking-wide text-[hsl(var(--text-muted))] mb-3">Form Progress</p>
              <div className="space-y-1.5">
                {STEPS.map((step) => {
                  const StepIcon = step.icon;
                  const done = currentStep > step.id;
                  const active = currentStep === step.id;
                  return (
                    <div key={step.id} className={`flex items-center gap-2 text-xs ${active ? 'text-teal-600 font-semibold' : done ? 'text-[hsl(var(--text-secondary))]' : 'text-[hsl(var(--text-muted))]'}`}>
                      {done ? <CheckCircle2 size={12} className="text-teal-500" /> : <StepIcon size={12} />}
                      {step.label}
                      {active && <span className="ml-auto text-2xs bg-teal-100 dark:bg-teal-950 text-teal-600 px-1.5 py-0.5 rounded-full">Current</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Review & Submit panel (full width) */}
        {currentStep === 7 && (
          <div className="mt-6 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-[hsl(var(--border))]">
              <div className="flex items-center gap-2.5">
                <CheckCircle2 size={16} className="text-teal-600" />
                <h2 className="text-base font-bold text-[hsl(var(--text-primary))]">Review & Submit</h2>
              </div>
            </div>
            <div className="p-5">
              <ReviewSubmit data={formData} onEdit={goToStep} />
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-[hsl(var(--border))]">
          <button
            onClick={goPrev}
            disabled={currentStep === 1}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[hsl(var(--border))] text-sm font-medium text-[hsl(var(--text-primary))] hover:bg-[hsl(var(--surface))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={15} /> Previous
          </button>

          <div className="flex items-center gap-1">
            {STEPS.map((step) => (
              <div
                key={step.id}
                className={`w-2 h-2 rounded-full transition-all ${
                  currentStep === step.id ? 'bg-teal-600 w-4' :
                  currentStep > step.id ? 'bg-teal-400' : 'bg-[hsl(var(--border))]'
                }`}
              />
            ))}
          </div>

          {currentStep < 7 ? (
            <button
              onClick={goNext}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 transition-colors"
            >
              Next <ChevronRight size={15} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle2 size={15} /> Submit Intake
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
